import type {
  PlatformEvent,
  Webhook,
  AgentAction,
  AgentScorecardRow,
  Candidate,
  CandidateInput,
  Company,
  CompanyInput,
  Contact,
  ContactInput,
  DecideRequest,
  Decision,
  FeedbackInput,
  FeedbackResult,
  Job,
  JobDraftResult,
  JobInput,
  Placement,
  PlacementEndRequest,
  PlacementInput,
  PlacementRateInput,
  PortalUser,
  PortalUserInput,
  StageChangeRequest,
  StageChangeResult,
  StaffDocument,
  Submission,
  SubmissionInput,
  SummaryDraftResult,
  Timesheet,
  TimesheetAlert,
  TimesheetEntry,
  TimesheetExport,
  TimesheetInput,
  ImportReport,
  ImportRequest,
  ImportSummary,
  StaffUser,
  StaffUserInput,
  TenantSettings,
  Brand,
  StyleGuide,
  StyleGuideInput,
  StyleSuggestion,
  Template,
  TemplateImport,
  TemplateImportInput,
  TemplateInput,
  TemplateVersionInput,
} from "@staffsmith/api-types";
import type { Auth } from "./auth.js";

// The staff app's view of the StaffSmith API. Types come from api/openapi.yaml
// (@staffsmith/api-types), so a contract change that breaks the app breaks the
// typecheck, not a user.

export type Page<T> = { data: T[]; next_cursor: string | null };
export type FieldError = { field: string; message: string };
export type DocumentOwnerType = "candidate" | "company" | "placement" | "agreement_document";

// An RFC 9457 problem from the API, readable in the UI.
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail?: string,
    readonly errors: FieldError[] = [],
    readonly requestId?: string,
    // The API wants a fresh sign-in before this action (RFC 9470 step-up).
    readonly stepUp = false,
  ) {
    super(detail ? `${title}: ${detail}` : title);
  }
}

// A record and the ETag to send back with If-Match when changing it.
export type Versioned<T> = { value: T; etag: string };

// A demo reset's record (demo-tenant.ts): its step of four, and how it ended.
export type DemoReset = {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  step: number;
  steps: string[];
  error: string | null;
  requested_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
};

export function createApi(
  baseUrl: string,
  auth: Auth,
  doFetch: typeof fetch = fetch.bind(globalThis),
) {
  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    headers: Record<string, string> = {},
  ): Promise<{ value: T; etag: string; status: number }> {
    const token = await auth.accessToken();
    if (!token) throw new ApiError(401, "Not signed in");
    // Every POST carries an Idempotency-Key, so a POST whose answer was lost to
    // the network is retried once safely: the API replays it rather than
    // creating twice.
    const idempotency: Record<string, string> =
      method === "POST" ? { "idempotency-key": crypto.randomUUID() } : {};
    const send = () =>
      doFetch(`${baseUrl}${path}`, {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          ...(body === undefined ? {} : { "content-type": "application/json" }),
          ...idempotency,
          ...headers,
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    const res = await send().catch((e: unknown) => {
      if (method !== "POST") throw e;
      return send();
    });
    const data = (await res.json().catch(() => undefined)) as Record<string, unknown> | undefined;
    if (!res.ok) {
      throw new ApiError(
        res.status,
        typeof data?.title === "string" ? data.title : `Request failed (${res.status})`,
        typeof data?.detail === "string" ? data.detail : undefined,
        Array.isArray(data?.errors) ? (data.errors as FieldError[]) : [],
        res.headers.get("x-request-id") ?? undefined,
        (res.headers.get("www-authenticate") ?? "").includes("insufficient_user_authentication"),
      );
    }
    return { value: data as T, etag: res.headers.get("etag") ?? "", status: res.status };
  }

  const get = async <T>(path: string) => (await request<T>("GET", path)).value;
  const getVersioned = async <T>(path: string): Promise<Versioned<T>> => {
    const r = await request<T>("GET", path);
    return { value: r.value, etag: r.etag };
  };
  const post = async <T>(path: string, body: unknown) =>
    (await request<T>("POST", path, body)).value;
  const patch = async <T>(path: string, etag: string, body: unknown) =>
    (await request<T>("PATCH", path, body, { "if-match": etag })).value;
  const q = (params: Record<string, string | undefined>) => {
    const s = new URLSearchParams(
      Object.entries(params).filter((e): e is [string, string] => Boolean(e[1])),
    ).toString();
    return s ? `?${s}` : "";
  };

  return {
    me: () =>
      get<{
        id: string;
        display_name: string;
        roles: string[];
        tenant: { name: string; demo?: boolean; brand?: Brand | null };
      }>("/v1/me"),

    // The firm's own admin (Phase 8b): staff sign-ins, roles and the firm's name.
    staffUsers: () => get<{ data: StaffUser[] }>("/v1/staff-users"),
    inviteStaff: (input: StaffUserInput) => post<StaffUser>("/v1/staff-users", input),
    setStaffRoles: async (id: string, roles: StaffUserInput["roles"]) =>
      (await request<StaffUser>("PUT", `/v1/staff-users/${id}/roles`, { roles })).value,
    disableStaff: (id: string) => post<StaffUser>(`/v1/staff-users/${id}/disable`, undefined),
    tenant: () => getVersioned<TenantSettings>("/v1/tenant"),
    renameTenant: (etag: string, name: string) =>
      patch<TenantSettings>("/v1/tenant", etag, { name }),
    // The firm's writing-style switches (Phase 8c; Q45).
    setStyleSettings: (etag: string, style: NonNullable<TenantSettings["style"]>) =>
      patch<TenantSettings>("/v1/tenant", etag, { style }),

    // CSV imports (Phase 8b): a dry run first, then a person runs it.
    runImport: (input: ImportRequest) => post<ImportReport>("/v1/imports", input),
    imports: () => get<{ data: ImportSummary[] }>("/v1/imports"),
    // Phase 14: contacts and jobs, import_agent's mapping, and rollback.
    runRecordImport: (input: {
      kind: "contacts" | "jobs";
      dry_run: boolean;
      file_name?: string;
      rows: Record<string, unknown>[];
    }) => post<ImportReport>("/v1/imports/records", input),
    suggestMapping: (kind: string, columns: string[]) =>
      post<{
        suggestions: { column: string; field: string | null; confidence: number; reason: string }[];
      }>("/v1/imports/suggest-mapping", { kind, columns }),
    rollbackImport: (id: string) =>
      post<{ set_aside: number; kept: { type: string; id: string; reason: string }[] }>(
        `/v1/imports/${id}/rollback`,
        {},
      ),
    // VMS (Phase 15).
    vmsPrograms: () => get<{ data: VmsProgram[] }>("/v1/vms-programs"),
    addVmsProgram: (input: Record<string, unknown>) =>
      post<{ id: string }>("/v1/vms-programs", input),
    syncVms: (programId: string) =>
      post<{ received: number; mapped: number }>(`/v1/vms-programs/${programId}/sync`, {}),
    vmsRequisitions: () => get<{ data: VmsRequisition[] }>("/v1/vms-requisitions"),
    vmsSubmittals: () => get<{ data: VmsSubmittal[] }>("/v1/vms-submittals"),
    proposeVmsSubmittal: (requisitionId: string, submissionId: string, billRate: number) =>
      post<{ id: string; decision_id: string }>(
        `/v1/vms-requisitions/${requisitionId}/submittals`,
        {
          submission_id: submissionId,
          bill_rate: billRate,
        },
      ),
    sendVmsSubmittal: (id: string) => post<{ status: string }>(`/v1/vms-submittals/${id}/send`, {}),

    jobs: (params: { status?: string; company_id?: string; cursor?: string } = {}) =>
      get<Page<Job>>(`/v1/jobs${q({ limit: "50", ...params })}`),
    job: (id: string) => getVersioned<Job>(`/v1/jobs/${id}`),
    createJob: (input: JobInput) => post<Job>("/v1/jobs", input),
    updateJob: (id: string, etag: string, input: JobInput) =>
      patch<Job>(`/v1/jobs/${id}`, etag, input),
    // The AI intake assistant's draft of a job from a client's request; saves nothing.
    draftJob: (requestText: string) =>
      post<JobDraftResult>("/v1/job-drafts", { request_text: requestText }),

    candidates: (params: { email?: string; cursor?: string } = {}) =>
      get<Page<Candidate>>(`/v1/candidates${q({ limit: "50", ...params })}`),
    candidate: (id: string) => getVersioned<Candidate>(`/v1/candidates/${id}`),
    createCandidate: (input: CandidateInput) => post<Candidate>("/v1/candidates", input),

    submissions: (params: { job_id?: string; cursor?: string } = {}) =>
      get<Page<Submission>>(`/v1/submissions${q({ limit: "200", ...params })}`),
    submission: (id: string) => get<Submission>(`/v1/submissions/${id}`),
    createSubmission: (input: SubmissionInput) => post<Submission>("/v1/submissions", input),
    changeStage: (id: string, input: StageChangeRequest) =>
      post<StageChangeResult>(`/v1/submissions/${id}/stage-changes`, input),
    recordFeedback: (id: string, input: FeedbackInput) =>
      post<FeedbackResult>(`/v1/submissions/${id}/feedback`, input),
    // The AI submission assistant drafts the client summary (saved unreviewed);
    // saving it here marks it reviewed by a person.
    draftSummary: (id: string, input: { notes?: string; replace?: boolean }) =>
      post<SummaryDraftResult>(`/v1/submissions/${id}/summary-drafts`, input),
    saveSummary: async (id: string, summary: string) =>
      (await request<Submission>("PUT", `/v1/submissions/${id}/summary`, { summary })).value,

    decisions: (params: { state?: string; cursor?: string } = {}) =>
      get<Page<Decision>>(`/v1/decisions${q({ limit: "200", ...params })}`),
    decision: (id: string) => get<Decision>(`/v1/decisions/${id}`),
    agentActions: (params: { agent?: string; record_id?: string; cursor?: string } = {}) =>
      get<Page<AgentAction>>(`/v1/agent-actions${q({ limit: "100", ...params })}`),
    decide: (id: string, input: DecideRequest) =>
      post<Decision>(`/v1/decisions/${id}/decide`, input),

    companies: (params: { cursor?: string } = {}) =>
      get<Page<Company>>(`/v1/companies${q({ limit: "200", ...params })}`),
    company: (id: string) => getVersioned<Company>(`/v1/companies/${id}`),
    createCompany: (input: CompanyInput) => post<Company>("/v1/companies", input),
    updateCompany: (id: string, etag: string, input: CompanyInput) =>
      patch<Company>(`/v1/companies/${id}`, etag, input),
    contacts: (companyId: string) =>
      get<Page<Contact>>(`/v1/companies/${companyId}/contacts?limit=200`),
    createContact: (companyId: string, input: ContactInput) =>
      post<Contact>(`/v1/companies/${companyId}/contacts`, input),
    // Client contacts as people (design v0.6, C4): the list, a contact's page,
    // moving companies, archiving, the timeline, and my inbox items.
    allContacts: (params: Record<string, string> = {}) =>
      get<{ data: ContactListRow[] }>(`/v1/contacts${q(params)}`),
    contactPage: (id: string) => get<ContactPage>(`/v1/contacts/${id}`),
    moveContact: (id: string, input: Record<string, unknown>) =>
      post<{ id: string }>(`/v1/contacts/${id}/move`, input),
    // Client agreements (design v0.6.1, C10).
    agreements: (params: Record<string, string> = {}) =>
      get<{ data: AgreementRow[] }>(`/v1/agreements${q(params)}`),
    agreement: (id: string) => get<AgreementPage>(`/v1/agreements/${id}`),
    createAgreement: (input: Record<string, unknown>) =>
      post<{ id: string }>("/v1/agreements", input),
    updateAgreement: (id: string, version: number, input: Record<string, unknown>) =>
      patch<{ id: string; version: number }>(`/v1/agreements/${id}`, `"${version}"`, input),
    setAgreementStatus: (id: string, status: string, reason?: string) =>
      post<unknown>(`/v1/agreements/${id}/status`, { status, ...(reason ? { reason } : {}) }),
    addAgreementDocument: (id: string, input: Record<string, unknown>) =>
      post<{ id: string }>(`/v1/agreements/${id}/documents`, input),
    updateAgreementDocument: async (id: string, input: Record<string, unknown>) =>
      (await request<unknown>("PATCH", `/v1/agreement-documents/${id}`, input)).value,
    addAgreementFile: (documentId: string, uploadedId: string) =>
      post<{ id: string }>(`/v1/agreement-documents/${documentId}/files`, {
        document_id: uploadedId,
      }),
    markExecuted: (fileId: string, reason?: string) =>
      post<unknown>(`/v1/agreement-files/${fileId}/executed`, reason ? { reason } : {}),
    addSigner: (documentId: string, input: Record<string, unknown>) =>
      post<{ id: string }>(`/v1/agreement-documents/${documentId}/signers`, input),
    addAgreementNote: (id: string, body: string, internal: boolean) =>
      post<{ id: string }>(`/v1/agreements/${id}/notes`, { body, internal }),
    // The Contract assistant's proposals (v0.6.1, C10c).
    // Key dates and reminders (v0.6.1, C10d).
    // Agreements and placements, submissions and claims (v0.6.1, C10e).
    placementAgreement: (placementId: string) =>
      get<PlacementAgreementView>(`/v1/placements/${placementId}/agreement`),
    agreementCoverage: (companyId: string, employmentType: string) =>
      get<{ covered: boolean; agreement: { id: string; title: string } | null }>(
        `/v1/agreements/coverage${q({ company_id: companyId, employment_type: employmentType })}`,
      ),
    decideGuaranteeClaim: (id: string, outcome: string, note?: string) =>
      post<unknown>(`/v1/guarantee-claims/${id}/decision`, { outcome, ...(note ? { note } : {}) }),
    // Tenant customization (v0.6.3, C11).
    customizationRegistry: () => get<CustomizationRegistry>("/v1/customization/registry"),
    customizationChanges: (params: Record<string, string> = {}) =>
      get<{ data: CustomizationChange[] }>(`/v1/customization/changes${q(params)}`),
    picklists: () =>
      get<{ data: (CustomizationRegistry["picklists"][number] & { object: string | null })[] }>(
        "/v1/customization/picklists",
      ),
    addPicklistValue: (list: string, input: Record<string, unknown>) =>
      post<{ key: string }>(`/v1/customization/picklists/${list}/values`, input),
    bulkAddPicklistValues: (list: string, text: string) =>
      post<{ added: number }>(`/v1/customization/picklists/${list}/bulk`, { text }),
    updatePicklistValue: async (list: string, key: string, input: Record<string, unknown>) =>
      (await request<unknown>("PATCH", `/v1/customization/picklists/${list}/values/${key}`, input))
        .value,
    deletePicklistValue: async (list: string, key: string) =>
      (await request<unknown>("DELETE", `/v1/customization/picklists/${list}/values/${key}`)).value,
    mergePicklistValues: (list: string, from: string[], into: string, dryRun: boolean) =>
      post<{ records: number; merge_id: string | null }>(
        `/v1/customization/picklists/${list}/merge`,
        { from, into, dry_run: dryRun },
      ),
    picklistMerges: () =>
      get<{
        data: {
          id: string;
          list_key: string;
          from: string[];
          into: string;
          records: number;
          merged_at: string;
          reversed_at: string | null;
          reversible: boolean;
        }[];
      }>("/v1/customization/picklist-merges"),
    reversePicklistMerge: (id: string) =>
      post<unknown>(`/v1/customization/picklist-merges/${id}/reverse`, {}),
    layouts: (object: string) =>
      get<{
        object: string;
        fields: { key: string; label: string; custom: boolean }[];
        starter: LayoutDefinition;
        layouts: {
          id: string;
          role: string;
          status: "draft" | "published";
          version: number;
          definition: LayoutDefinition;
          editing: string | null;
          published_at: string | null;
          updated_at: string;
        }[];
      }>(`/v1/customization/objects/${object}/layouts`),
    saveLayoutDraft: async (object: string, role: string, definition: LayoutDefinition) =>
      (
        await request<unknown>(
          "PUT",
          `/v1/customization/objects/${object}/layouts/draft${q({ role })}`,
          {
            definition,
          },
        )
      ).value,
    publishLayoutDraft: (object: string, role: string) =>
      post<{ version: number }>(
        `/v1/customization/objects/${object}/layouts/draft/publish${q({ role })}`,
        {},
      ),
    discardLayoutDraft: async (object: string, role: string) =>
      (
        await request<unknown>(
          "DELETE",
          `/v1/customization/objects/${object}/layouts/draft${q({ role })}`,
        )
      ).value,
    numbering: () =>
      get<{ data: { object: string; pattern: string | null; next: string | null }[] }>(
        "/v1/customization/numbering",
      ),
    setNumbering: async (object: string, pattern: string, nextSeq?: number) =>
      (
        await request<unknown>("PUT", `/v1/customization/numbering/${object}`, {
          pattern,
          ...(nextSeq ? { next_seq: nextSeq } : {}),
        })
      ).value,
    displayOptions: () => get<DisplayOptions>("/v1/customization/display"),
    setDisplayOptions: async (input: Partial<DisplayOptions>) =>
      (await request<DisplayOptions>("PUT", "/v1/customization/display", input)).value,
    exportConfiguration: () => get<Record<string, unknown>>("/v1/customization/export"),
    importConfiguration: (input: {
      config: unknown;
      dry_run?: boolean;
      confirm?: boolean;
      archive_missing?: boolean;
    }) => post<ConfigChanges>("/v1/customization/import", input),
    starterConfigurations: () =>
      get<{ data: { key: string; label: string; updated_at: string }[] }>(
        "/v1/customization/starters",
      ),
    applyStarter: (key: string, dryRun: boolean) =>
      post<ConfigChanges>(
        `/v1/customization/starters/${key}/apply`,
        dryRun ? { dry_run: true } : { confirm: true },
      ),
    customizationFields: (object: string) =>
      get<ObjectFields>(`/v1/customization/objects/${object}/fields`),
    setStandardField: async (object: string, key: string, input: Record<string, unknown>) =>
      (
        await request<unknown>(
          "PUT",
          `/v1/customization/objects/${object}/standard-fields/${key}`,
          input,
        )
      ).value,
    createCustomField: (object: string, input: Record<string, unknown>) =>
      post<{ key: string; sensitive: boolean; warning?: string }>(
        `/v1/customization/objects/${object}/fields`,
        input,
      ),
    updateCustomField: async (object: string, key: string, input: Record<string, unknown>) =>
      (await request<unknown>("PATCH", `/v1/customization/objects/${object}/fields/${key}`, input))
        .value,
    customFieldAction: (
      object: string,
      key: string,
      action: "archive" | "restore" | "sensitive-clear" | "sensitive-clear/decision",
      body: Record<string, unknown> = {},
    ) => post<unknown>(`/v1/customization/objects/${object}/fields/${key}/${action}`, body),
    agreementDates: (days = 120) =>
      get<{ days: number; data: { date: string; what: string; agreement: AgreementRow }[] }>(
        `/v1/agreements/upcoming?days=${days}`,
      ),
    agreementSettings: () =>
      get<{
        agreement_reminder_days: number[];
        certificate_reminder_days: number;
        agreement_missing_mode: "warn" | "block";
      }>("/v1/agreement-settings"),
    setAgreementSettings: async (input: {
      agreement_reminder_days: number[];
      certificate_reminder_days: number;
      agreement_missing_mode?: "warn" | "block";
    }) => (await request<unknown>("PUT", "/v1/agreement-settings", input)).value,
    agreementProposals: (id: string) =>
      get<{ data: AgreementProposalRun[] }>(`/v1/agreements/${id}/proposals`),
    decideProposal: (id: string, body: { decision: "accept" | "reject"; value?: unknown }) =>
      post<{ decision: string }>(`/v1/agreement-proposals/${id}/decision`, body),
    agreementTerms: (id: string, asOf: string) =>
      get<AgreementTerms>(`/v1/agreements/${id}/terms?as_of=${asOf}`),
    setAgreementTerms: async (documentId: string, type: string, terms: Record<string, unknown>) =>
      (
        await request<unknown>("PUT", `/v1/agreement-documents/${documentId}/terms/${type}`, {
          terms,
        })
      ).value,
    setRiskTerms: async (id: string, items: Record<string, unknown>[]) =>
      (await request<unknown>("PUT", `/v1/agreements/${id}/risk-terms`, { items })).value,
    addInsuranceRequirement: (id: string, input: Record<string, unknown>) =>
      post<{ id: string }>(`/v1/agreements/${id}/insurance-requirements`, input),
    addInsuranceCertificate: (requirementId: string, input: Record<string, unknown>) =>
      post<{ id: string }>(`/v1/insurance-requirements/${requirementId}/certificates`, input),
    archiveContact: (id: string) => post<unknown>(`/v1/contacts/${id}/archive`, {}),
    // Contact status (v0.6.2, C4.1): one, or several at once.
    setContactStatus: async (id: string, change: StatusChange) =>
      (await request<{ status: ContactStatus }>("PUT", `/v1/contacts/${id}/status`, change)).value,
    setContactStatuses: (ids: string[], change: StatusChange) =>
      post<{ data: { id: string; changed: boolean; error?: string }[] }>("/v1/contacts/status", {
        ids,
        ...change,
      }),
    contactSettings: () => get<{ inactive_contacts_in_sequences: boolean }>("/v1/contact-settings"),
    setContactSettings: async (value: boolean) =>
      (
        await request<{ inactive_contacts_in_sequences: boolean }>("PUT", "/v1/contact-settings", {
          inactive_contacts_in_sequences: value,
        })
      ).value,
    updateContact: (id: string, version: number, input: ContactInput) =>
      patch<Contact>(`/v1/contacts/${id}`, `"${version}"`, input),
    updateContactPerson: (id: string, input: Record<string, unknown>) =>
      request<unknown>("PATCH", `/v1/contacts/${id}/person`, input),
    companyActivities: (companyId: string) =>
      get<{ data: ActivityRow[] }>(`/v1/companies/${companyId}/activities`),
    logActivity: (input: Record<string, unknown>) => post<{ id: string }>("/v1/activities", input),
    inboxItems: () => get<{ data: InboxItem[] }>("/v1/inbox-items"),
    inboxItemDone: (id: string) => post<unknown>(`/v1/inbox-items/${id}/done`, {}),

    // Template Preview, writing constraints and editing a draft (design v0.6, C5 and C6).
    previewTemplate: (
      templateId: string,
      version: number,
      record: { object: string; id: string } | null,
    ) =>
      post<TemplatePreviewResult>(`/v1/templates/${templateId}/versions/${version}/preview`, {
        ...(record ? { record } : {}),
      }),
    // Your writing samples and style profile (design v0.6, C6b).
    writingSamples: () => get<{ data: WritingSample[] }>("/v1/writing-samples"),
    addWritingSample: (text: string) => post<WritingSample>("/v1/writing-samples", { text }),
    uploadWritingSample: async (file: File) => {
      const started = await post<{
        upload_url: string;
        upload_headers: Record<string, string>;
        sample: WritingSample;
      }>("/v1/writing-samples/uploads", { file_name: file.name });
      const put = await doFetch(started.upload_url, {
        method: "PUT",
        headers: { "content-type": "application/octet-stream", ...started.upload_headers },
        body: file,
      });
      if (!put.ok) throw new ApiError(put.status, "The file didn't upload; try again");
      return started.sample;
    },
    tagWritingSample: async (id: string, outcome: string | null) =>
      (await request<unknown>("PUT", `/v1/writing-samples/${id}/outcome`, { outcome })).value,
    deleteWritingSample: async (id: string) =>
      (await request<unknown>("DELETE", `/v1/writing-samples/${id}`)).value,
    writingProfile: () => get<WritingProfileView>("/v1/writing-profile"),
    draftWritingProfile: () => post<{ profile_id: string }>("/v1/writing-profile/draft", {}),
    saveWritingProfile: async (id: string, content: StyleProfileContent, approve: boolean) =>
      (await request<unknown>("PUT", `/v1/writing-profile/${id}`, { content, approve })).value,
    // Placement extensions (design v0.6, C7).
    extensions: (params: Record<string, string> = {}) =>
      get<{ data: ExtensionActivity[] }>(`/v1/extensions${q(params)}`),
    placementExtension: (placementId: string) =>
      get<{
        activity: ExtensionActivity | null;
        events: { occurred_at: string; actor_type: string; what: string }[];
      }>(`/v1/placements/${placementId}/extension`),
    recordExtensionAnswer: (activityId: string, input: Record<string, unknown>) =>
      post<{ id: string }>(`/v1/extensions/${activityId}/answers`, input),
    extensionSettings: () => get<ExtensionSettings>("/v1/extension-settings"),
    saveExtensionSettings: async (input: Record<string, unknown>) =>
      (await request<unknown>("PUT", "/v1/extension-settings", input)).value,
    // Company roles, directory sync and the access review (design v0.6, C8).
    companyRoles: () =>
      get<{
        data: CompanyRole[];
        unassigned: { id: string; display_name: string; email: string }[];
        rules: { id: string; source: string; match_value: string; role_id: string }[];
      }>("/v1/company-roles"),
    createCompanyRole: (input: Record<string, unknown>) =>
      post<{ id: string }>("/v1/company-roles", input),
    updateCompanyRole: async (id: string, input: Record<string, unknown>) =>
      (await request<unknown>("PUT", `/v1/company-roles/${id}`, input)).value,
    addRoleMember: (roleId: string, input: Record<string, unknown>) =>
      post<unknown>(`/v1/company-roles/${roleId}/members`, input),
    endRoleMember: (roleId: string, memberId: string) =>
      post<unknown>(`/v1/company-roles/${roleId}/members/${memberId}/end`, {}),
    addRoleRule: (input: Record<string, unknown>) =>
      post<{ id: string }>("/v1/role-mapping-rules", input),
    removeRoleRule: async (id: string) =>
      (await request<unknown>("DELETE", `/v1/role-mapping-rules/${id}`)).value,
    syncDirectory: () =>
      post<{
        matched: number;
        not_in_platform: string[];
        roles_added: number;
        roles_ended: number;
        leavers: number;
        reassigned: number;
      }>("/v1/directory/sync", {}),
    // The access review as a CSV file, saved by the browser.
    downloadAccessReview: async () => {
      const token = await auth.accessToken();
      const res = await doFetch(`${baseUrl}/v1/access-review`, {
        headers: { authorization: `Bearer ${token ?? ""}` },
      });
      if (!res.ok) throw new ApiError(res.status, "The access review couldn't be made");
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = "access-review.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
    // The customization history as a CSV file (v0.6.3, C11f).
    downloadCustomizationHistory: async () => {
      const token = await auth.accessToken();
      const res = await doFetch(`${baseUrl}/v1/customization/changes/export`, {
        headers: { authorization: `Bearer ${token ?? ""}` },
      });
      if (!res.ok) throw new ApiError(res.status, "The history couldn't be exported");
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = "customization-history.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
    // The skills library (design v0.6, C9a).
    skills: (params: Record<string, string> = {}) =>
      get<{ data: LibrarySkill[] }>(`/v1/skills${q(params)}`),
    addSkill: (input: Record<string, unknown>) => post<{ key: string }>("/v1/skills", input),
    changeSkill: async (key: string, input: Record<string, unknown>) =>
      (await request<unknown>("PATCH", `/v1/skills/${encodeURIComponent(key)}`, input)).value,
    skillCoreUpdates: () =>
      get<{
        data: {
          version: number;
          source: string;
          changes: {
            change: string;
            skill: string;
            into?: string;
            name?: string;
            choice: string | null;
          }[];
        }[];
      }>("/v1/skills/core-updates"),
    decideSkillCoreUpdate: async (version: number, key: string, choice: "accepted" | "skipped") =>
      (
        await request<unknown>(
          "PUT",
          `/v1/skills/core-updates/${version}/${encodeURIComponent(key)}`,
          { choice },
        )
      ).value,
    // Skill sets, matching and the Skills assistant (design v0.6, C9c to C9f).
    jobSkills: (jobId: string) => get<{ data: JobSkill[] }>(`/v1/jobs/${jobId}/skills`),
    parseJobSkills: (jobId: string) =>
      post<{ found: number }>(`/v1/jobs/${jobId}/skills/parse`, {}),
    setJobSkill: async (
      jobId: string,
      key: string,
      input: { weight: number; must_have: boolean; min_years?: number | null },
    ) =>
      (await request<unknown>("PUT", `/v1/jobs/${jobId}/skills/${encodeURIComponent(key)}`, input))
        .value,
    removeJobSkill: async (jobId: string, key: string) =>
      (await request<unknown>("DELETE", `/v1/jobs/${jobId}/skills/${encodeURIComponent(key)}`))
        .value,
    jobMatches: (jobId: string) =>
      get<{
        data: (MatchScore & { id: string; name: string })[];
        manual_review: { id: string; name: string; reason: "declined" | "not_asked" }[];
      }>(`/v1/jobs/${jobId}/matches`),
    candidateSkills: (candidateId: string) =>
      get<{
        data: CandidateSkill[];
        suggestions: { id: string; change: "add" | "remove"; name: string; note: string | null }[];
      }>(`/v1/candidates/${candidateId}/skills`),
    parseCandidateSkills: (candidateId: string) =>
      post<{ found: number }>(`/v1/candidates/${candidateId}/skills/parse`, {}),
    reviewSkillSuggestion: (id: string, outcome: "accepted" | "declined") =>
      post<unknown>(`/v1/skill-suggestions/${id}/review`, { outcome }),
    matchingJobs: (candidateId: string) =>
      get<{
        data: (MatchScore & { id: string; title: string; company: string })[];
        manual_review: boolean;
      }>(`/v1/candidates/${candidateId}/matching-jobs`),
    parseAllSkillSets: () =>
      post<{ jobs: number; candidates: number; remaining: number }>("/v1/skills/parse-all", {}),
    skillProposals: () =>
      get<{
        data: {
          id: string;
          kind: string;
          name: string;
          evidence: { summary?: string };
          created_at: string;
        }[];
      }>("/v1/skills/proposals"),
    runSkillProposals: () => post<{ proposed: number }>("/v1/skills/proposals/run", {}),
    decideSkillProposal: (id: string, outcome: "approved" | "rejected", name?: string) =>
      post<unknown>(`/v1/skills/proposals/${id}/decision`, { outcome, name }),
    constraints: () => get<{ data: WritingConstraint[] }>("/v1/writing-constraints"),
    createConstraint: (input: Record<string, unknown>) =>
      post<WritingConstraint>("/v1/writing-constraints", input),
    updateConstraint: async (id: string, input: Record<string, unknown>) =>
      (await request<WritingConstraint>("PATCH", `/v1/writing-constraints/${id}`, input)).value,
    editMessage: async (id: string, input: { body: string; subject?: string }) =>
      (
        await request<{ id: string; constraint_flags: ConstraintFlag[] }>(
          "PATCH",
          `/v1/messages/${id}`,
          input,
        )
      ).value,

    agentScorecard: () => get<{ data: AgentScorecardRow[] }>("/v1/agent-scorecard"),

    placements: (params: { status?: string; cursor?: string } = {}) =>
      get<Page<Placement>>(`/v1/placements${q({ limit: "200", ...params })}`),
    placement: (id: string) => get<Placement>(`/v1/placements/${id}`),
    createPlacement: (input: PlacementInput) => post<Placement>("/v1/placements", input),
    addPlacementRate: (id: string, input: PlacementRateInput) =>
      post<Placement>(`/v1/placements/${id}/rates`, input),
    // Only opens a decision; the end date changes when a person approves it.
    requestPlacementEnd: (id: string, input: PlacementEndRequest) =>
      post<Decision>(`/v1/placements/${id}/end-requests`, input),

    timesheets: (params: { placement_id?: string; status?: string } = {}) =>
      get<Page<Timesheet>>(`/v1/timesheets${q({ limit: "200", ...params })}`),
    createTimesheet: (input: TimesheetInput) => post<Timesheet>("/v1/timesheets", input),
    replaceTimesheetEntries: async (id: string, entries: TimesheetEntry[]) =>
      (await request<Timesheet>("PUT", `/v1/timesheets/${id}/entries`, { entries })).value,
    // Staff submit for the consultant; only the client approves.
    submitTimesheet: (id: string) => post<Timesheet>(`/v1/timesheets/${id}/submit`, {}),

    // A starter report from the reporting schema (refreshed daily).
    report: (name: string) =>
      get<{ data: Record<string, unknown>[]; refreshed: boolean }>(`/v1/reports/${name}`),

    // Webhooks and the event log (tenant admins).
    webhooks: () => get<{ data: Webhook[] }>("/v1/webhooks"),
    createWebhook: (input: { url: string; event_types: string[]; description?: string }) =>
      post<Webhook & { signing_secret?: string }>("/v1/webhooks", input),
    removeWebhook: async (id: string) => {
      await request<unknown>("DELETE", `/v1/webhooks/${id}`);
    },
    webhookDeliveries: (id: string) =>
      get<{
        data: {
          id: string;
          event_type: string;
          status: string;
          attempts: number;
          last_status_code: number | null;
          last_error: string | null;
          delivered_at: string | null;
          created_at: string;
        }[];
      }>(`/v1/webhooks/${id}/deliveries`),
    events: (params: { type?: string; cursor?: string } = {}) =>
      get<Page<PlatformEvent>>(`/v1/events${q({ limit: "100", ...params })}`),

    // Portal sign-ins for consultants and client contacts (QUESTIONS.md Q32).
    portalUsers: (params: { candidate_id?: string; contact_id?: string }) =>
      get<{ data: PortalUser[] }>(`/v1/portal-users${q(params)}`),
    createPortalUser: (input: PortalUserInput) => post<PortalUser>("/v1/portal-users", input),
    disablePortalUser: (id: string) => post<PortalUser>(`/v1/portal-users/${id}/disable`, {}),

    agentScorecardPeriod: () =>
      get<{
        minutes_saved: number;
        ai_cost_per_placement: string | null;
        conversion: Record<
          "agent_sourced" | "human_sourced" | "agent_screened" | "human_screened",
          { submissions: number; interviews: number; placements: number }
        >;
      }>("/v1/agent-scorecard/period"),
    timesheetAlerts: () => get<{ data: TimesheetAlert[] }>("/v1/timesheet-alerts"),
    dismissTimesheetAlert: (id: string) => post<unknown>(`/v1/timesheet-alerts/${id}/dismiss`, {}),
    runTimesheetAgent: () =>
      post<{ missing: number; anomalies: number }>("/v1/timesheet-alerts/runs", {}),
    timesheetExports: () => get<{ data: TimesheetExport[] }>("/v1/timesheet-exports"),
    timesheetExport: (id: string) => get<TimesheetExport>(`/v1/timesheet-exports/${id}`),
    createTimesheetExport: () => post<TimesheetExport>("/v1/timesheet-exports", {}),

    documents: (ownerType: DocumentOwnerType, ownerId: string) =>
      get<Page<StaffDocument>>(`/v1/documents${q({ owner_type: ownerType, owner_id: ownerId })}`),
    document: (id: string) => get<StaffDocument>(`/v1/documents/${id}`),
    // Two steps: the API records the document and signs an upload URL, then the
    // file goes straight to storage (it never passes through the API).
    uploadDocument: async (
      owner: { type: DocumentOwnerType; id: string },
      kind: "resume" | "cover_letter" | "signed_form" | "other" | "agreement",
      file: File,
    ) => {
      const started = await post<{
        document_id: string;
        upload_url: string;
        upload_headers?: Record<string, string>;
      }>("/v1/documents", {
        owner_type: owner.type,
        owner_id: owner.id,
        kind,
        file_name: file.name,
        content_type: file.type,
      });
      const res = await doFetch(started.upload_url, {
        method: "PUT",
        // A firm with its own key needs the encryption headers too (ADR 0019).
        headers: { "content-type": file.type, ...started.upload_headers },
        body: file,
      });
      if (!res.ok) throw new ApiError(res.status, "The upload didn't finish; try again");
      return started.document_id;
    },

    // Templates and writing style (Phase 8c).
    templates: (params: { agent?: string; purpose?: string; status?: string } = {}) =>
      get<{ data: Template[] }>(`/v1/templates${q(params)}`),
    template: (id: string) => get<Template>(`/v1/templates/${id}`),
    createTemplate: (input: TemplateInput) => post<Template>("/v1/templates", input),
    draftTemplateVersion: (id: string, input: TemplateVersionInput) =>
      post<Template>(`/v1/templates/${id}/versions`, input),
    mapPlaceholders: async (
      id: string,
      n: number,
      placeholders: { token: string; field: string | null }[],
    ) =>
      (
        await request<Template>("PUT", `/v1/templates/${id}/versions/${n}/placeholders`, {
          placeholders,
        })
      ).value,
    decideTemplateVersion: (
      id: string,
      n: number,
      outcome: "approved" | "sent_back",
      note?: string,
    ) =>
      post<Template>(`/v1/templates/${id}/versions/${n}/decision`, {
        outcome,
        ...(note ? { note } : {}),
      }),
    retireTemplate: (id: string) => post<Template>(`/v1/templates/${id}/retire`, {}),
    mergeFields: () => get<{ data: { field: string; label: string }[] }>("/v1/merge-fields"),
    templateImports: () => get<{ data: TemplateImport[] }>("/v1/template-imports"),
    templateImport: (id: string) => get<TemplateImport>(`/v1/template-imports/${id}`),
    // Two steps, like a document: the API checks the name and signs an upload URL,
    // then the file goes straight to storage. Reading starts once it's scanned.
    importTemplateFile: async (
      file: File,
      defaults: Omit<TemplateImportInput, "file_name"> = {},
    ) => {
      const started = await post<{
        upload_url: string;
        upload_headers: Record<string, string>;
        import: TemplateImport;
      }>("/v1/template-imports", { file_name: file.name, ...defaults });
      const res = await doFetch(started.upload_url, {
        method: "PUT",
        headers: { "content-type": "application/octet-stream", ...started.upload_headers },
        body: file,
      });
      if (!res.ok) throw new ApiError(res.status, "The upload didn't finish; try again");
      return started.import;
    },
    styleGuides: (scope = "tenant", scopeKey = "") =>
      get<{ data: StyleGuide[] }>(`/v1/style-guides${q({ scope, scope_key: scopeKey })}`),
    draftStyleGuide: (input: StyleGuideInput) => post<StyleGuide>("/v1/style-guides", input),
    setPersonalStyle: async (optIn: boolean) =>
      (await request<{ opt_in: boolean }>("PUT", "/v1/personal-style", { opt_in: optIn })).value,
    styleSuggestions: () => get<{ data: StyleSuggestion[] }>("/v1/style-suggestions"),
    askForStyleSuggestions: () =>
      post<{ outcome: string; suggestions: StyleSuggestion[] }>("/v1/style-suggestions/runs", {}),
    decideStyleSuggestion: (id: string, outcome: "accepted" | "rejected") =>
      post<{ style_guide_draft_id: string | null }>(`/v1/style-suggestions/${id}/decision`, {
        outcome,
      }),
    decideStyleGuide: (id: string, outcome: "approved" | "sent_back", note?: string) =>
      post<StyleGuide>(`/v1/style-guides/${id}/decision`, { outcome, ...(note ? { note } : {}) }),

    // Onboarding (Phase 9): the board, a case and what each task waits on.
    onboardingCases: (status = "open") =>
      get<{ data: OnboardingCaseRow[] }>(`/v1/onboarding-cases${q({ status })}`),
    onboardingCase: (id: string) => get<OnboardingCase>(`/v1/onboarding-cases/${id}`),
    updateOnboardingTask: (caseId: string, taskId: string, status: string, note?: string) =>
      post<OnboardingCase>(`/v1/onboarding-cases/${caseId}/tasks/${taskId}`, {
        status,
        ...(note ? { note } : {}),
      }),
    clearOnboardingCase: (id: string) =>
      post<OnboardingCase>(`/v1/onboarding-cases/${id}/clear`, {}),
    sendForSignature: (caseId: string, taskId: string) =>
      post<Envelope>(`/v1/onboarding-cases/${caseId}/signatures`, { task_id: taskId }),
    refreshEnvelope: (id: string) => post<Envelope>(`/v1/signature-envelopes/${id}/refresh`, {}),
    voidEnvelope: (id: string) => post<Envelope>(`/v1/signature-envelopes/${id}/void`, {}),
    orderScreening: (caseId: string, taskId: string) =>
      post<ScreeningOrder>(`/v1/onboarding-cases/${caseId}/screening`, { task_id: taskId }),
    screeningOrder: (id: string) => get<ScreeningOrder>(`/v1/screening-orders/${id}`),
    screeningResult: (id: string) =>
      get<{ result: string | null; report_document_id: string | null }>(
        `/v1/screening-orders/${id}/result`,
      ),
    askPreAdverse: (id: string, reason?: string) =>
      post<{ decision_id: string }>(`/v1/screening-orders/${id}/adverse-action/pre-adverse`, {
        ...(reason ? { reason } : {}),
      }),
    askFinalAdverse: (id: string) =>
      post<{ decision_id: string }>(`/v1/screening-orders/${id}/adverse-action/final`, {}),
    fcraNotices: () => get<Record<string, string>>("/v1/fcra-notices"),
    startI9: (caseId: string) => post<I9Record>(`/v1/onboarding-cases/${caseId}/i9`, {}),
    i9Record: (id: string) => get<I9Record>(`/v1/i9-records/${id}`),
    refreshI9: (id: string) => post<I9Record>(`/v1/i9-records/${id}/refresh`, {}),
    acceptI9Documents: (
      id: string,
      docs: { list_a?: string; list_b?: string; list_c?: string; documents_expire_on?: string },
    ) => post<I9Record>(`/v1/i9-records/${id}/accept-documents`, docs),
    openEVerify: (id: string) => post<I9Record>(`/v1/i9-records/${id}/e-verify`, {}),
    credentials: (candidateId: string) =>
      get<{ data: Credential[] }>(`/v1/candidates/${candidateId}/credentials`),
    addCredential: (candidateId: string, input: Record<string, string>) =>
      post<Credential>(`/v1/candidates/${candidateId}/credentials`, input),
    verifyCredential: (id: string, outcome: "verified" | "rejected", note?: string) =>
      post<Credential>(`/v1/credentials/${id}/verify`, { outcome, ...(note ? { note } : {}) }),
    attachCredential: (caseId: string, taskId: string, credentialId: string) =>
      post<{ status: string }>(`/v1/onboarding-cases/${caseId}/tasks/${taskId}/credential`, {
        credential_id: credentialId,
      }),
    expiringCredentials: (withinDays = 60) =>
      get<{ data: ExpiringCredential[] }>(
        `/v1/credentials/expiring${q({ within_days: String(withinDays) })}`,
      ),
    dismissCredentialReminder: (id: string) =>
      post<{ status: string }>(`/v1/credential-reminders/${id}/dismiss`, {}),
    // Payroll (Phase 10): admins and finance.
    payrollBatches: () => get<{ data: PayrollBatchRow[] }>("/v1/payroll-batches"),
    payrollBatch: (id: string) => get<PayrollBatch>(`/v1/payroll-batches/${id}`),
    buildPayrollBatch: (throughWeek?: string) =>
      post<PayrollBatch>("/v1/payroll-batches", throughWeek ? { through_week: throughWeek } : {}),
    submitPayrollBatch: (id: string) =>
      post<{ decision_id: string }>(`/v1/payroll-batches/${id}/submit`, {}),
    cancelPayrollBatch: (id: string) => post<PayrollBatch>(`/v1/payroll-batches/${id}/cancel`, {}),
    exportPayrollBatch: (id: string) =>
      post<{ status: string }>(`/v1/payroll-batches/${id}/export`, {}),
    payrollResults: (id: string) =>
      post<{ status: string; statements: { id: string; net: number }[] }>(
        `/v1/payroll-batches/${id}/results`,
        {},
      ),
    offCyclePayments: () => get<{ data: OffCyclePayment[] }>("/v1/off-cycle-payments"),
    requestOffCycle: (input: { candidate_id: string; amount: string; reason: string }) =>
      post<{ decision_id: string }>("/v1/off-cycle-payments", input),
    exportOffCycle: (id: string) =>
      post<OffCyclePayment>(`/v1/off-cycle-payments/${id}/export`, {}),
    paySetup: (candidateId: string) => get<PaySetup>(`/v1/candidates/${candidateId}/pay-setup`),
    addPaySetup: (candidateId: string, input: Record<string, unknown>) =>
      post<PaySetup>(`/v1/candidates/${candidateId}/pay-setup`, input),
    // Communications (Phase 11): the inbox, consent, sequences and campaigns.
    threads: (params: Record<string, string> = {}) =>
      get<{ data: ThreadRow[] }>(`/v1/message-threads${q(params)}`),
    thread: (id: string) => get<Thread>(`/v1/message-threads/${id}`),
    startThread: (input: Record<string, unknown>) => post<Thread>("/v1/message-threads", input),
    draftMessage: (threadId: string, body: string) =>
      post<Thread>(`/v1/message-threads/${threadId}/messages`, { body }),
    sendMessage: (id: string) => post<Thread>(`/v1/messages/${id}/send`, {}),
    askForReply: (threadId: string, notes?: string) =>
      post<{ message_id: string; kind: string }>(
        `/v1/message-threads/${threadId}/draft-reply`,
        notes ? { notes } : {},
      ),
    approveMessage: (id: string, kind?: "rejection" | "offer") =>
      post<{ approved: boolean }>(`/v1/messages/${id}/approve`, kind ? { kind } : {}),
    consents: (personType: string, personId: string) =>
      get<ConsentRecord>(`/v1/consents/${personType}/${personId}`),
    recordConsent: (personType: string, personId: string, channel: string, status: string) =>
      post<{ status: string }>(`/v1/consents/${personType}/${personId}`, { channel, status }),
    sequences: () => get<{ data: SequenceRow[] }>("/v1/sequences"),
    createSequence: (input: {
      name: string;
      channel: string;
      steps: { template_id: string; delay_days: number }[];
    }) => post<{ id: string }>("/v1/sequences", input),
    enroll: (sequenceId: string, personType: string, personId: string) =>
      post<{ id: string }>(`/v1/sequences/${sequenceId}/enrollments`, {
        person_type: personType,
        person_id: personId,
      }),
    campaigns: () => get<{ data: CampaignRow[] }>("/v1/sequence-campaigns"),
    decideCampaign: (id: string, outcome: "approved" | "declined") =>
      post<{ status: string; enrolled: number }>(`/v1/sequence-campaigns/${id}/decide`, {
        outcome,
      }),
    // Search, duplicates and long lists (Phase 12).
    searchCandidates: (query: string) =>
      get<{ mode: string; data: SearchHit[] }>(`/v1/candidate-search${q({ q: query })}`),
    reindexSearch: () => post<{ embedded: number }>("/v1/candidate-search/reindex", {}),
    savedSearches: () => get<{ data: SavedSearch[] }>("/v1/saved-searches"),
    saveSearch: (name: string, query: string, alerts: boolean) =>
      post<{ id: string }>("/v1/saved-searches", { name, query, alerts }),
    searchAlerts: () => get<{ data: SearchAlert[] }>("/v1/saved-search-alerts"),
    // The firm's side of the platform console (Phase 18).
    features: () => get<Record<string, boolean>>("/v1/features"),
    // The demo tenant and its one-press reset (Phase 8; demo tenants only).
    demoTenant: () =>
      get<{
        name: string;
        resets_enabled: boolean;
        resets_done: number;
        last_reset: DemoReset | null;
        current_reset: DemoReset | null;
        counts: Record<string, number>;
        sign_ins: { email: string; app: string; who: string }[];
      }>("/v1/demo-tenant"),
    resetDemo: () => post<{ reset_id: string }>("/v1/demo-tenant/resets", {}),
    demoReset: (id: string) => get<DemoReset>(`/v1/demo-tenant/resets/${id}`),
    // The status page (Phase 19): live incidents StaffSmith has told firms about.
    platformStatus: () =>
      get<{
        operational: boolean;
        incidents: {
          id: string;
          title: string;
          status: string;
          public_summary: string;
          declared_at: string;
        }[];
      }>("/v1/platform-status"),
    supportGrants: () =>
      get<{
        grants: {
          id: string;
          reason: string;
          starts_at: string;
          expires_at: string;
          revoked_at: string | null;
        }[];
        uses: { id: string; grant_id: string; action: string; occurred_at: string }[];
      }>("/v1/support-grants"),
    grantSupport: (hours: number, reason: string) =>
      post<{ id: string }>("/v1/support-grants", { hours, reason }),
    revokeSupport: (id: string) => post<{ id: string }>(`/v1/support-grants/${id}/revoke`, {}),
    billingInvoices: () =>
      get<{
        data: {
          id: string;
          period_start: string;
          lines: { item: string; quantity: number; unit_usd: number; amount_usd: number }[];
          total_usd: number;
          issued_at: string | null;
        }[];
      }>("/v1/billing/invoices"),
    // Privacy operations (Phase 17): requests, holds and retention; admins only.
    privacyRequests: () => get<{ data: PrivacyRequest[] }>("/v1/privacy-requests"),
    privacyRequest: (id: string) => get<PrivacyRequest>(`/v1/privacy-requests/${id}`),
    recordPrivacyRequest: (input: {
      kind: string;
      channel: string;
      requester_name: string;
      requester_email?: string;
      details?: string;
    }) => post<PrivacyRequest>("/v1/privacy-requests", input),
    verifyPrivacyRequest: (id: string, method: string, candidateId: string, note: string) =>
      post<PrivacyRequest>(`/v1/privacy-requests/${id}/verify`, {
        method,
        candidate_id: candidateId,
        note,
      }),
    refusePrivacyRequest: (id: string, reason: string) =>
      post<PrivacyRequest>(`/v1/privacy-requests/${id}/refuse`, { reason }),
    preparePrivacyRequest: (id: string) =>
      post<{ decision_id: string }>(`/v1/privacy-requests/${id}/prepare`, {}),
    completePrivacyRequest: (id: string) =>
      post<PrivacyRequest>(`/v1/privacy-requests/${id}/complete`, {}),
    privacyPackage: (id: string) => get<unknown>(`/v1/privacy-requests/${id}/package`),
    legalHolds: (candidateId?: string) =>
      get<{ data: LegalHold[] }>(`/v1/legal-holds${q({ candidate_id: candidateId })}`),
    placeLegalHold: (candidateId: string, reason: string) =>
      post<{ id: string }>("/v1/legal-holds", { candidate_id: candidateId, reason }),
    liftLegalHold: (id: string, reason: string) =>
      post<{ id: string }>(`/v1/legal-holds/${id}/lift`, { reason }),
    retentionSchedules: () => get<{ data: RetentionSchedule[] }>("/v1/retention-schedules"),
    setRetention: async (category: string, keepDays: number) =>
      (
        await request<{ category: string }>("PUT", `/v1/retention-schedules/${category}`, {
          keep_days: keepDays,
        })
      ).value,
    // A decided screening proposal as one of the firm's own eval cases (Phase 16).
    createEvalCase: (decisionId: string, about: string) =>
      post<{ id: string }>("/v1/eval-cases", { decision_id: decisionId, about }),
    markAlertSeen: (id: string) =>
      post<{ status: string }>(`/v1/saved-search-alerts/${id}/seen`, {}),
    scanDuplicates: () =>
      post<{ found: number; added: number }>("/v1/candidate-duplicates/scan", {}),
    duplicates: () => get<{ data: DuplicatePair[] }>("/v1/candidate-duplicates"),
    dismissDuplicate: (id: string) =>
      post<{ status: string }>(`/v1/candidate-duplicates/${id}/dismiss`, {}),
    mergeCandidates: (survivorId: string, mergedId: string, take: Record<string, string>) =>
      post<{ id: string }>("/v1/candidate-merges", {
        survivor_id: survivorId,
        merged_id: mergedId,
        take,
      }),
    merges: () => get<{ data: MergeRow[] }>("/v1/candidate-merges"),
    undoMerge: (id: string) => post<{ undone: boolean }>(`/v1/candidate-merges/${id}/undo`, {}),
    buildLongList: (jobId: string) =>
      post<{ id: string; members: number }>(`/v1/jobs/${jobId}/longlists`, {}),
    longList: (jobId: string) => get<LongList>(`/v1/jobs/${jobId}/longlist`),
    // Documents (Phase 13): the resume read, confirming fields, client copies.
    resumeParse: (candidateId: string) =>
      get<ResumeParse>(`/v1/candidates/${candidateId}/resume-parse`),
    confirmResumeFields: (id: string, fields: string[]) =>
      post<{ status: string }>(`/v1/resume-parses/${id}/confirm`, { fields }),
    clientCopy: (documentId: string) =>
      get<{ file_name: string; text: string; removed: number }>(
        `/v1/documents/${documentId}/client-copy`,
      ),
  };
}

// Onboarding's routes are internal (x-internal) and carry no response schemas
// in the contract yet, so their shapes are written here, matching
// services/ats/src/onboarding.ts, signatures.ts, screening.ts, i9.ts and
// credentials.ts.
export type OnboardingCaseRow = {
  id: string;
  placement_id: string;
  status: string;
  start_date: string | null;
  consultant: string;
  client: string;
  tasks: number;
  tasks_done: number;
};
export type OnboardingTask = {
  id: string;
  kind: string;
  label: string;
  done_by: "consultant" | "staff";
  status: string;
  due_on: string | null;
  note: string | null;
  completed_at: string | null;
  chased_on: string | null;
  chase_message: string | null;
  credential_id: string | null;
  envelope: { id: string; status: string } | null;
  screening_order: { id: string; status: string } | null;
  i9_record_id: string | null;
};
export type OnboardingCase = {
  id: string;
  placement_id: string;
  status: string;
  consultant: string;
  candidate_id: string;
  client: string;
  start_date: string | null;
  cleared_at: string | null;
  tasks: OnboardingTask[];
};
export type Envelope = { id: string; status: string; document_kind: string };
export type ScreeningOrder = {
  id: string;
  kind: string;
  status: string;
  fcra: {
    pre_adverse_at: string | null;
    waiting_period_ends: string | null;
    final_adverse_at: string | null;
  };
};
export type I9Record = {
  id: string;
  status: string;
  section1_completed_at: string | null;
  section2: {
    list_a: string | null;
    list_b: string | null;
    list_c: string | null;
    documents_expire_on: string | null;
    accepted_at: string | null;
  };
  e_verify: { task_id: string | null; status: string | null; notice?: string };
};
export type Credential = {
  id: string;
  kind: string;
  name: string;
  issuer: string | null;
  expires_on: string | null;
  status: string;
};
export type ExpiringCredential = {
  id: string;
  candidate_id: string;
  consultant: string;
  name: string;
  expires_on: string;
  days_left: number;
  reminders: { id: string; threshold_days: number; draft_message: string | null }[];
};

export type Api = ReturnType<typeof createApi>;

// Payroll's routes are internal too (services/ats/src/payroll.ts, off-cycle.ts,
// pay-setup.ts).
export type PayrollBatchRow = {
  id: string;
  status: string;
  through_week: string;
  line_count: number;
  blocking_issues: number;
  gross: number;
  currency: string;
  created_at: string;
};
export type PayrollLine = {
  id: string;
  candidate_id: string;
  consultant: string;
  week_start: string;
  pay_type: string | null;
  work_state: string | null;
  regular_hours: number;
  overtime_hours: number;
  double_time_hours: number;
  holiday_hours: number;
  pto_hours: number;
  pay_rate: number | null;
  gross: number;
  issues: string[];
  blocking: boolean;
};
export type PayrollBatch = PayrollBatchRow & {
  decision_id: string | null;
  approved_at: string | null;
  agent_summary: string | null;
  lines: PayrollLine[];
};
export type OffCyclePayment = {
  id: string;
  candidate_id: string;
  amount: number;
  currency: string;
  reason: string;
  status: string;
  decision_id: string | null;
  created_at: string;
};
export type PaySetupRow = {
  id: string;
  effective_from: string;
  pay_type: string;
  provider: string;
  work_state: string;
  pay_frequency: string;
  overtime_exempt: boolean;
  reason: string | null;
};
export type PaySetup = { current: PaySetupRow | null; history: PaySetupRow[] };

// Communications' routes are internal too (services/ats/src/messaging.ts,
// sequences.ts, comms.ts).
export type ThreadRow = {
  id: string;
  person_type: string;
  person_id: string;
  person_name: string | null;
  channel: string;
  subject: string | null;
  status: string;
  inbound: number;
  // The last message came in: someone is waiting for a reply.
  awaiting_reply: boolean;
  last_message_at: string | null;
};
export type ThreadMessage = {
  id: string;
  direction: "inbound" | "outbound";
  status: string;
  subject: string | null;
  body: string;
  kind: string;
  ai_drafted: boolean;
  // Writing rules an AI draft breaks (design v0.6, C6a); it can't be sent until fixed.
  constraint_flags: ConstraintFlag[] | null;
  approved_at: string | null;
  blocked_reason: string | null;
  sent_at: string | null;
  received_at: string | null;
  created_at: string | null;
};
export type Thread = {
  id: string;
  person: { type: string; id: string };
  channel: string;
  subject: string | null;
  messages: ThreadMessage[];
};
export type ConsentRecord = {
  email: string | null;
  sms: string | null;
  history: { id: string; channel: string; status: string; source: string; recorded_at: string }[];
};
export type SequenceRow = {
  id: string;
  name: string;
  channel: string;
  active: boolean;
  steps: number;
  active_enrollments: number;
};
export type CampaignRow = {
  id: string;
  sequence: string;
  person_count: number;
  status: string;
  enrolled: number | null;
  created_at: string;
};

// Search's routes are internal too (services/ats/src/search.ts,
// saved-searches.ts, duplicates.ts, sourcing.ts).
// Privacy operations (services/ats/src/privacy.ts) are internal routes.
export type PrivacyRequest = {
  id: string;
  kind: "access" | "correction" | "deletion" | "opt_out";
  channel: string;
  candidate_id: string | null;
  requester_name: string;
  requester_email: string | null;
  details: string | null;
  received_at: string;
  due_on: string;
  status: "received" | "verified" | "pending_approval" | "approved" | "closed" | "refused";
  verified_method: string | null;
  verified_at: string | null;
  verification_note: string | null;
  findings: {
    counts: Record<string, number>;
    on_legal_hold: boolean;
    erase?: string[];
    kept?: { category: string; until: string | null; reason: string }[];
  } | null;
  draft_response: string | null;
  decision_id: string | null;
  closed_at: string | null;
  refusal_reason: string | null;
  certificate?: {
    id: string;
    erased: Record<string, Record<string, number>>;
    kept: { category: string; until: string | null; reason: string }[];
    files_pending: string[];
    issued_at: string;
    digest: string;
  } | null;
};
export type LegalHold = {
  id: string;
  candidate_id: string;
  reason: string;
  placed_at: string;
  lifted_at: string | null;
  lift_reason: string | null;
};
export type RetentionSchedule = {
  category: string;
  keep_days: number;
  after: string;
  basis: string;
  firm_set: boolean;
};
export type SearchHit = {
  candidate_id: string;
  name: string;
  headline: string | null;
  score: number;
  reasons: string[];
};
export type SavedSearch = { id: string; name: string; query: string; alerts: boolean };
export type SearchAlert = {
  id: string;
  name: string;
  candidate_ids: string[];
  status: string;
  created_at: string;
};
export type DuplicateSide = {
  id: string;
  name: string;
  first_name: string;
  last_name: string;
  headline: string | null;
  skills: unknown;
  location: unknown;
};
export type DuplicatePair = {
  id: string;
  score: number;
  reasons: string[];
  found_by: string;
  a: DuplicateSide;
  b: DuplicateSide;
};
export type MergeRow = {
  id: string;
  survivor_id: string;
  merged_id: string;
  merged_at: string;
  undo_until: string;
  undone_at: string | null;
};
export type LongList = {
  id: string;
  decision_id: string;
  ranking: string;
  members: {
    candidate_id: string;
    name: string;
    headline: string | null;
    reasons: string[];
    rank?: number;
    score?: number;
  }[];
};
export type ResumeParse = {
  id: string;
  status: string;
  fields: {
    field: string;
    value: string | string[];
    confidence: number;
    applied: boolean;
    confirmed?: boolean;
  }[];
};

// VMS routes are internal too (services/ats/src/vms.ts).
export type VmsProgram = {
  id: string;
  name: string;
  provider: string;
  company_id: string;
  bill_rate_cap: number | null;
  markup_max_pct: number | null;
  submittals_per_req: number | null;
  owns_billing: boolean;
};
export type VmsRequisition = {
  id: string;
  program: string;
  external_ref: string;
  title: string;
  bill_rate_max: number | null;
  job_id: string | null;
  status: string;
};
export type VmsSubmittal = {
  id: string;
  title: string;
  candidate: string;
  bill_rate: number;
  rule_checks: { rule: string; ok: boolean; detail: string }[];
  status: string;
  decision_id: string | null;
  vms_status: string | null;
  created_at: string;
};

// Client agreements (design v0.6.1, C10).
export const AGREEMENT_TYPES = [
  "direct_hire",
  "contract",
  "contract_to_hire",
  "managed_services",
  "other",
] as const;
export const AGREEMENT_STATUSES = [
  "draft",
  "in_negotiation",
  "out_for_signature",
  "active",
  "expiring",
  "expired",
  "terminated",
  "superseded",
] as const;
export const AGREEMENT_DOC_KINDS = [
  "amendment",
  "rate_schedule",
  "sow",
  "addendum",
  "renewal",
  "termination",
] as const;
type KeyDates = {
  nonrenewal_deadline: string | null;
  next_renewal: string | null;
  next_key_date: string | null;
};
export type AgreementRow = KeyDates & {
  id: string;
  title: string;
  number: string | null;
  company: { id: string; name: string };
  types: string[];
  status: string;
  effective_date: string | null;
  end_date: string | null;
  evergreen: boolean;
  owner: { id: string; name: string | null } | null;
};
export type AgreementDocument = {
  id: string;
  kind: string;
  title: string;
  status: string;
  effective_date: string | null;
  end_date: string | null;
  files: {
    id: string;
    document_id: string;
    version: number;
    label: string | null;
    is_executed: boolean;
    replaced_reason: string | null;
    file_name: string;
    scan_status: string;
    uploaded_by: string | null;
    at: string | null;
  }[];
  signers: {
    id: string;
    party: string;
    name: string;
    title: string | null;
    contact_id: string | null;
    user_id: string | null;
    signed_on: string | null;
    method: string | null;
  }[];
};
export type AgreementPage = KeyDates & {
  id: string;
  version: number;
  company: { id: string; name: string };
  title: string;
  number: string | null;
  client_ref: string | null;
  types: string[];
  sub_type: string | null;
  tags: string[];
  tenant_entity: string | null;
  client_entity: string | null;
  third_party: string | null;
  status: string;
  effective_date: string | null;
  end_date: string | null;
  evergreen: boolean;
  initial_term_months: number | null;
  auto_renew: boolean;
  renewal_term_months: number | null;
  nonrenewal_notice_days: number | null;
  term_conv: boolean;
  term_conv_notice_days: number | null;
  term_cause_notice_days: number | null;
  governing_law: string | null;
  venue: string | null;
  dispute_resolution: string | null;
  owner_user_id: string | null;
  legal_contact_user_id: string | null;
  reminder_days: number[] | null;
  signature_date: string | null;
  supersedes: { id: string; title: string; status: string } | null;
  superseded_by: { id: string; title: string; status: string } | null;
  documents: AgreementDocument[];
  notes: { id: string; body: string; internal: boolean; at: string | null; by: string | null }[];
  timeline: { id: string; kind: string; summary: string; at: string | null; by: string }[];
  risk_terms: {
    item: string;
    label: string;
    options: string[];
    status: "included" | "not_included" | "not_specified";
    value: string | null;
    clause_ref: string | null;
    note: string | null;
  }[];
  insurance: {
    id: string;
    kind: string;
    minimum_amount: string | null;
    currency: string | null;
    additional_insured: boolean;
    certificates: {
      id: string;
      insurer: string | null;
      amount: string | null;
      currency: string | null;
      expires_on: string | null;
      document_id: string | null;
    }[];
  }[];
  can: { edit: boolean; approve: boolean; read_commercial: boolean };
};
// Client contacts as people (design v0.6, C4), each place with a status (C4.1).
export const CONTACT_STATUSES = ["sourced", "active", "inactive", "archived"] as const;
export type ContactStatus = (typeof CONTACT_STATUSES)[number];
export type StatusChange = { status: ContactStatus; reason?: string; confirm?: boolean };
export type ContactListRow = {
  id: string;
  person_id: string;
  first_name: string;
  last_name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  roles: string[];
  company: { id: string; name: string };
  owner: { id: string; name: string | null } | null;
  status: ContactStatus;
  last_activity_at: string | null;
};
export type ActivityRow = {
  id: string;
  type: string;
  occurred_at: string | null;
  summary: string;
  company_id: string | null;
  contact_id: string | null;
  contact_name: string | null;
  by: string | null;
};
export type ContactPage = {
  contact: { id: string; version: number; status: ContactStatus };
  attached_to: string[];
  status_history: {
    id: string;
    at: string | null;
    company_name: string;
    from: ContactStatus | null;
    to: ContactStatus;
    reason: string | null;
    by: string;
  }[];
  person: {
    id: string;
    first_name: string;
    last_name: string;
    personal_notes: string | null;
    preferred_channel: string | null;
  };
  affiliations: {
    id: string;
    company: { id: string; name: string };
    title: string | null;
    roles: string[];
    email: string | null;
    phone: string | null;
    started_on: string | null;
    ended_on: string | null;
    moved_to_id: string | null;
    archived: boolean;
    status: ContactStatus;
    owner: { id: string; name: string | null } | null;
    current: boolean;
  }[];
  activities: ActivityRow[];
  reviewed: {
    id: string;
    at: string | null;
    submission_id: string;
    job: { id: string; title: string };
    candidate_name: string;
  }[];
  approved_timesheets: {
    id: string;
    week_start: string | null;
    approved_at: string | null;
    consultant_name: string;
  }[];
  placements: {
    id: string;
    consultant_name: string;
    company_name: string;
    end_date: string | null;
  }[];
};
export type InboxItem = {
  id: string;
  kind:
    | "contact_left"
    | "new_lead"
    | "reassign_contact"
    | "extension_escalated"
    | "backfill_needed"
    | "redeployment"
    | "extension_reply"
    | "agreement_date";
  label: string;
  record_object: "contact" | "company" | "placement" | "agreement";
  record_id: string;
  created_at: string | null;
};

// Template Preview (design v0.6, C5).
export type TemplatePreviewResult = {
  channel: "email" | "sms" | "script" | "document";
  filled_from: string;
  subject: string | null;
  text: string;
  html: string | null;
  missing: { field: string; label: string }[];
  unmapped: string[];
  sms?: { characters: number; segments: number; encoding: string };
};

// Writing constraints (design v0.6, C6a).
export type WritingConstraint = {
  id: string;
  title: string;
  rule_text: string;
  example_right: string | null;
  example_wrong: string | null;
  topic: string | null;
  channels: string[];
  agents: string[];
  detector: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
};
export type ConstraintFlag = { id: string; title: string; why: string };

// Writing samples and style profiles (design v0.6, C6b).
export type WritingSample = {
  id: string;
  source: "paste" | "upload" | "email";
  file_name: string | null;
  status: "pending_upload" | "ready" | "refused" | "failed";
  refusal: string | null;
  body: string | null;
  redactions: number;
  outcome: string | null;
  created_at: string;
};
export type StyleProfileContent = {
  tone: string;
  length: string;
  structure: string;
  openings: string;
  sign_offs: string;
  use: string[];
  avoid: string[];
  quotes: string[];
};
type ProfileView = {
  id: string;
  number: number;
  status: string;
  content: StyleProfileContent;
  approved_at: string | null;
  samples_changed: boolean;
};
export type WritingProfileView = {
  approved: ProfileView | null;
  draft: ProfileView | null;
  sample_count: number;
};

// Placement extensions (design v0.6, C7).
export type ExtensionActivity = {
  id: string;
  placement_id: string;
  consultant_name: string;
  company: { id: string; name: string };
  end_date: string | null;
  days_left: number;
  status: string;
  owner: { kind: string; name: string | null };
  escalation_name: string | null;
  consultant_answer: string | null;
  client_answer: string | null;
  not_extending_reason: string | null;
  decision_id: string | null;
  conversion: boolean;
};
export type ExtensionSettings = {
  open_days: number;
  remind_days: number[];
  owner_kind: "user" | "agent";
  owner_user_id: string | null;
  escalation_user_id: string | null;
  consultant_outreach: string;
  client_outreach: string;
};

// Company roles (design v0.6, C8).
export type CompanyRole = {
  id: string;
  name: string;
  description: string | null;
  responsibilities: string | null;
  team: string | null;
  reports_to_role_id: string | null;
  default_access: unknown;
  decision_authority: unknown;
  expected_skills: unknown;
  training_link: string | null;
  fallback_owner_user_id: string | null;
  members: {
    id: string;
    user_id: string;
    name: string;
    starts_on: string | null;
    ends_on: string | null;
    acting_for: string | null;
    source: string;
  }[];
};

// The skills library (design v0.6, C9a).
export type LibrarySkill = {
  key: string;
  name: string;
  family: string | null;
  grouping: string | null;
  aliases: string[];
  hidden: boolean;
  source: "core" | "firm";
};

export type JobSkill = {
  skill_key: string;
  name: string;
  weight: number;
  must_have: boolean;
  min_years: number | null;
  source: "parsed" | "staff";
};

export type CandidateSkill = {
  skill_key: string;
  name: string;
  evidence: string;
  years: number | null;
  recent: boolean;
  keyword_only: boolean;
  source: "parsed" | "staff";
};

// A match is a band and its breakdown; never a bare number on screen.
export type MatchScore = {
  score: number;
  band: "strong" | "good" | "partial" | "weak";
  breakdown: {
    required: { have: number; of: number };
    missing: string[];
    strengths: string[];
    summary: string;
  };
};

// The commercial terms in force on a date, each with its source (v0.6.1, C10b).
export type AgreementTerms = {
  as_of: string;
  effective: Record<
    string,
    Record<string, { value: unknown; source: { id: string; title: string } }>
  >;
  documents: { agreement_document_id: string; type: string; terms: Record<string, unknown> }[];
};

// One run of the Contract assistant over one file (v0.6.1, C10c).
export type AgreementProposalRun = {
  id: string;
  agreement_document_id: string;
  document_title: string;
  file_name: string;
  at: string;
  missing: string[];
  proposals: {
    id: string;
    field: string;
    value: unknown;
    page: number | null;
    clause: string | null;
    confidence: number;
    decision: "pending" | "accepted" | "edited" | "rejected";
    decided_value: unknown;
    decided_by: string | null;
  }[];
};

// The agreement behind a placement (v0.6.1, C10e).
export type PlacementAgreementView = {
  agreement: { id: string; title: string; status: string; end_date: string | null } | null;
  warnings: string[];
  fee: { amount: number | null; basis: string | null } | null;
  guarantee_end_date: string | null;
  conversion: { hours_worked: number; current_fee: number; from_hours: number } | null;
  guarantee_claim: {
    id: string;
    ended_on: string | null;
    remedy: string | null;
    status: "open" | "decided";
    outcome: string | null;
    note: string | null;
  } | null;
};

// Tenant customization (v0.6.3, C11).
export type CustomizationRegistry = {
  objects: { key: string; label: string; custom_fields: number }[];
  picklists: {
    key: string;
    label: string;
    kind: "system" | "tenant";
    object: string | null;
    field: string | null;
    values: {
      key: string;
      label: string;
      description: string | null;
      color: string | null;
      sort: number;
      status: "active" | "retired";
      is_default: boolean;
      built_in: boolean;
      system_value: string | null;
      parent_value: string | null;
    }[];
  }[];
  settings: {
    key: string;
    module: string;
    label: string;
    type: string;
    options: string[] | null;
    default: unknown;
    value: unknown;
    edited_at: string;
  }[];
};
export type CustomizationChange = {
  id: string;
  area: string;
  object: string | null;
  key: string;
  action: string;
  before: unknown;
  after: unknown;
  at: string;
  by: string | null;
};
export type ObjectFields = {
  object: string;
  label: string;
  standard: {
    key: string;
    system: boolean;
    built_in_label: string;
    label: string;
    plural: string | null;
    help: string | null;
    hidden: boolean;
    required: boolean;
    default: unknown;
  }[];
  custom: {
    key: string;
    type: string;
    label: string;
    plural: string | null;
    help: string | null;
    properties: Record<string, unknown>;
    status: "active" | "archived";
    archived_at: string | null;
    version: number;
  }[];
  limit: number;
  used: number;
};

// A record layout (v0.6.3, C11d).
export type LayoutDefinition = {
  sections: { id: string; title: string; collapsed: boolean; columns: 1 | 2; fields: string[] }[];
  list: { columns: string[]; sort: { field: string; direction: "asc" | "desc" } | null };
};

// The firm's display options (v0.6.3, C11e).
export type DisplayOptions = {
  date_format: "MM/DD/YYYY" | "DD/MM/YYYY" | "YYYY-MM-DD";
  time_zone: string;
  currency: string;
  week_start: "monday" | "sunday";
  working_hours: { start: string; end: string };
};

// What a configuration import changes (v0.6.3, C11f).
export type ConfigChanges = {
  applied: boolean;
  changes: { area: string; key: string; action: string; detail?: string }[];
};
